import React from 'react';
import { Link } from 'react-router-dom';

export default function PrivacyPolicy() {
  return (
    <div style={styles.page}>
      <div style={styles.card}>

        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerIcon}>🔒</div>
          <h1 style={styles.headerTitle}>Tech Assist – Privacy Policy</h1>
          <p style={styles.headerSub}>Last Updated: 01-Jan-2026</p>
        </div>

        {/* Content */}
        <div style={styles.content}>

          {/* Introduction */}
          <Section title="Introduction">
            <p style={styles.text}>
              Tech Assist ("we", "our", "us") provides audio call-based support services connecting
              customers with expert agents. This Privacy Policy explains how we collect, use, share,
              and protect your information in accordance with applicable data protection regulations
              and Play Store policy requirements.
            </p>
          </Section>

          {/* Information We Collect */}
          <Section title="Information We Collect">
            <ul style={styles.list}>
              {[
                'Mobile number',
                'OTP verification details (via third-party WhatsApp OTP provider)',
                'Profile details (name, language preference, optional profile location)',
                'Package purchase details and transaction history',
                'Support ticket messages',
                'FCM token (for OTP delivery, notifications, and incoming call alerts)',
              ].map((item, i) => (
                <li key={i} style={styles.listItem}>
                  <span style={styles.bullet}>•</span> {item}
                </li>
              ))}
            </ul>
            <div style={styles.noteBox}>
              <strong>We do NOT collect</strong> your IP address, device model, analytics data, or crash logs.
            </div>
          </Section>

          {/* Permissions */}
          <Section title="Permissions We Use & Why">
            <div style={styles.permGrid}>
              {[
                { perm: 'RECORD_AUDIO', desc: 'Required for in-app audio calls between customers and agents' },
                { perm: 'POST_NOTIFICATIONS', desc: 'For alerts, OTP delivery, and incoming call notifications' },
                { perm: 'ACCESS_FINE_LOCATION', desc: 'Used to fetch latitude/longitude during login and when user updates their profile' },
              ].map((item, i) => (
                <div key={i} style={styles.permRow}>
                  <span style={styles.permTag}>{item.perm}</span>
                  <span style={styles.permDesc}>– {item.desc}</span>
                </div>
              ))}
            </div>
          </Section>

          {/* How We Use Your Data */}
          <Section title="How We Use Your Data">
            <div style={styles.usageGrid}>
              {[
                'OTP-based login and verification',
                'Profile creation and management',
                'Audio call functionality and routing',
                'Balance deduction based on call duration',
                'Processing package purchases',
                'Providing customer support',
              ].map((item, i) => (
                <div key={i} style={styles.usageItem}>
                  <span style={styles.checkMark}>✓</span> {item}
                </div>
              ))}
            </div>
          </Section>

          {/* Data Sharing */}
          <Section title="Data Sharing">
            <p style={styles.label}>Your data is shared ONLY with the following service providers:</p>
            <ul style={styles.list}>
              {[
                'WhatsApp OTP provider (for authentication)',
                'Firebase Cloud Messaging (for push notifications)',
                'Agora / CallKeep (for audio call functionality)',
                'Our secure backend servers',
              ].map((item, i) => (
                <li key={i} style={styles.listItem}>
                  <span style={styles.bullet}>•</span> {item}
                </li>
              ))}
            </ul>
            <div style={styles.warningBox}>
              ⚠️ &nbsp;<strong>We DO NOT sell your data to advertisers or third parties.</strong>
            </div>
          </Section>

          {/* Data Retention */}
          <Section title="Data Retention">
            <div style={styles.retentionGrid}>
              {[
                { title: 'Call history & transactions', desc: 'Retained as required for account records' },
                { title: 'Profile data', desc: 'Stored until the user requests deletion' },
              ].map((item, i) => (
                <div key={i} style={styles.retentionRow}>
                  <span style={styles.retentionTitle}>{item.title}</span>
                  <span style={styles.retentionDesc}>{item.desc}</span>
                </div>
              ))}
            </div>
          </Section>

          {/* Security */}
          <Section title="Security">
            <div style={styles.securityBox}>
              <span style={styles.securityIcon}>🛡️</span>
              <p style={styles.text}>
                We use HTTPS encryption, secure storage, and JWT token authentication to ensure your
                data remains protected at all times.
              </p>
            </div>
          </Section>

          {/* User Rights */}
          <Section title="Your Rights">
            <p style={styles.text}>You have the right to:</p>
            <ul style={styles.list}>
              {[
                'Access your personal data',
                'Request correction of inaccurate data',
                'Request deletion of your account and associated data',
                'Withdraw consent for data processing at any time',
              ].map((item, i) => (
                <li key={i} style={styles.listItem}>
                  <span style={styles.bullet}>•</span> {item}
                </li>
              ))}
            </ul>
            <p style={{ ...styles.text, marginTop: 12 }}>
              To delete your account, use our{' '}
              <Link to="/delete-account" style={styles.link}>Account Deletion page</Link>.
            </p>
          </Section>

          {/* Contact */}
          <Section title="Contact Us">
            <div style={styles.contactBox}>
              <div style={styles.contactRow}>
                <strong style={styles.contactLabel}>Email:</strong>
                <a href="mailto:webranditsolutions@gmail.com" style={styles.link}>webranditsolutions@gmail.com</a>
              </div>
              <div style={styles.contactRow}>
                <strong style={styles.contactLabel}>App:</strong>
                <span>Tech Assist – Customer</span>
              </div>
            </div>
          </Section>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <p style={styles.footerText}>
            © {new Date().getFullYear()} Tech Assist. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={styles.section}>
      <h2 style={styles.sectionTitle}>{title}</h2>
      {children}
    </section>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f0f4ff 0%, #e8f5e9 100%)',
    padding: '40px 16px',
    fontFamily: "'Segoe UI', Roboto, sans-serif",
  },
  card: {
    maxWidth: 860,
    margin: '0 auto',
    background: '#fff',
    borderRadius: 20,
    boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
    overflow: 'hidden',
  },
  header: {
    background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
    padding: '48px 40px',
    textAlign: 'center',
    color: '#fff',
  },
  headerIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  headerTitle: {
    fontSize: 32,
    fontWeight: 800,
    margin: 0,
    letterSpacing: '-0.5px',
  },
  headerSub: {
    marginTop: 12,
    fontSize: 15,
    color: 'rgba(255,255,255,0.8)',
    fontWeight: 500,
  },
  content: {
    padding: '40px 48px',
  },
  section: {
    marginBottom: 40,
    paddingBottom: 32,
    borderBottom: '1px solid #f0f0f0',
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: '#1e293b',
    marginBottom: 16,
    paddingLeft: 14,
    borderLeft: '4px solid #3b82f6',
  },
  text: {
    color: '#4b5563',
    lineHeight: 1.8,
    fontSize: 15,
    margin: 0,
  },
  list: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  listItem: {
    color: '#4b5563',
    fontSize: 15,
    padding: '6px 0',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
  },
  bullet: {
    color: '#3b82f6',
    fontWeight: 700,
    flexShrink: 0,
    marginTop: 2,
  },
  noteBox: {
    marginTop: 16,
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: 10,
    padding: '14px 18px',
    color: '#1e40af',
    fontSize: 14,
  },
  permGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  permRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    background: '#f8fafc',
    padding: '12px 16px',
    borderRadius: 10,
    flexWrap: 'wrap',
  },
  permTag: {
    fontFamily: 'monospace',
    fontWeight: 700,
    color: '#1d4ed8',
    fontSize: 13,
    background: '#dbeafe',
    padding: '3px 10px',
    borderRadius: 6,
    whiteSpace: 'nowrap',
  },
  permDesc: {
    color: '#4b5563',
    fontSize: 14,
  },
  usageGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: 10,
  },
  usageItem: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    padding: '12px 16px',
    color: '#374151',
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  checkMark: {
    color: '#16a34a',
    fontWeight: 700,
    fontSize: 16,
  },
  label: {
    color: '#374151',
    fontWeight: 600,
    marginBottom: 12,
    fontSize: 15,
  },
  warningBox: {
    marginTop: 16,
    background: '#fff7ed',
    border: '1px solid #fed7aa',
    borderRadius: 10,
    padding: '14px 18px',
    color: '#9a3412',
    fontWeight: 600,
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
  },
  retentionGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  retentionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: '1px solid #f0f0f0',
    flexWrap: 'wrap',
    gap: 8,
  },
  retentionTitle: {
    fontWeight: 600,
    color: '#1e293b',
    fontSize: 15,
  },
  retentionDesc: {
    color: '#6b7280',
    fontStyle: 'italic',
    fontSize: 14,
  },
  securityBox: {
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: 12,
    padding: '20px 24px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16,
  },
  securityIcon: {
    fontSize: 28,
    flexShrink: 0,
  },
  contactBox: {
    background: '#f8fafc',
    borderRadius: 12,
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  contactRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    fontSize: 15,
    color: '#374151',
  },
  contactLabel: {
    minWidth: 60,
    color: '#1e293b',
  },
  link: {
    color: '#3b82f6',
    textDecoration: 'none',
    fontWeight: 500,
  },
  footer: {
    background: '#f8fafc',
    padding: '20px 40px',
    borderTop: '1px solid #e5e7eb',
    textAlign: 'center',
  },
  footerText: {
    color: '#9ca3af',
    fontSize: 13,
    margin: 0,
  },
};
